import { Link, Popover } from '@ksyos/design-system';
import { useState } from 'react';
import type { SortDirection } from '../../../../utils/sortDirection';
import type { IServiceOrganizationProvider } from '../../../interfaces/serviceOrganizationProvider';
import type { IExpandedTableRow, ITableHeader, ITableRow } from '../../../userDataManagement/SelectableSortableTable';
import { getDisplayName } from '../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../translate';
import { SelectableSortableTable } from '../../../userDataManagement/SelectableSortableTable';

interface IPersonalSettingsPreferenceListConsultationTableProps {
    isReadOnly?: boolean;
    onChange: (selectedServiceOrganizationProviderOrganizationIds: string[]) => Promise<void>;
    onSort: (sortKey: string, sortDirection: SortDirection) => Promise<void>;
    selectedServiceOrganizationProviderOrganizationIds: string[];
    serviceOrganizationProviders: IServiceOrganizationProvider[];
    sortDirection: SortDirection;
    sortKey: string;
}

export function PersonalSettingsPreferenceListConsultationTable(
    props: IPersonalSettingsPreferenceListConsultationTableProps
) {
    const defaultServiceProviderCount = 4;
    const [allServiceProvidersExpandedProviderIds, setAllServiceProvidersExpandedProviderIds] = useState<string[]>([]);

    if (props.serviceOrganizationProviders.length === 0) {
        return null;
    }

    return (
        <SelectableSortableTable
            isReadOnly={props.isReadOnly}
            onSelect={props.onChange}
            onSort={props.onSort}
            selectedRowKeys={props.selectedServiceOrganizationProviderOrganizationIds}
            sortDirection={props.sortDirection}
            sortKey={props.sortKey}
            tableHeaders={getTableHeaders()}
            tableRows={getTableRows()}
        />
    );

    function getTableHeaders() {
        return [
            {
                columnKey: 'organization.name',
                children: translateKey('organization.organizationSpecialist'),
                isSortable: true,
            },
            { columnKey: 'organization.city', children: translateKey('common.city'), isSortable: true },
            {
                columnKey: 'specialist.profession',
                children: translateKey('personalSettings.preferenceList.specialistProfession'),
            },
            {
                columnKey: 'specialist.specialization',
                children: translateKey('personalSettings.preferenceList.specialization'),
            },
        ] as ITableHeader[];
    }

    function getTableRows() {
        return props.serviceOrganizationProviders.map((preferredProvidingOrganization) => {
            return {
                rowKey: preferredProvidingOrganization.organization.id,
                cells: [
                    { style: { fontWeight: 500 }, children: preferredProvidingOrganization.organization.name },
                    { children: preferredProvidingOrganization.organization.city },
                    {},
                    {},
                ],
                expandedTableRows: getExpandedTableRows(preferredProvidingOrganization),
            };
        }) as ITableRow[];
    }

    function getExpandedTableRows(preferredProvidingOrganization: IServiceOrganizationProvider) {
        const expandedServiceProvidersToShow = allServiceProvidersExpandedProviderIds.includes(
            preferredProvidingOrganization.organization.id
        )
            ? preferredProvidingOrganization.serviceProviders
            : preferredProvidingOrganization.serviceProviders.slice(0, defaultServiceProviderCount);

        const expandedTableRows: IExpandedTableRow[] = expandedServiceProvidersToShow.map(
            (serviceProvider, index, array) => {
                return {
                    rowKey: `expandedTableRow_${preferredProvidingOrganization.organization.id}_${serviceProvider.id}`,
                    cells: [
                        { children: getDisplayName(serviceProvider.employment.practitioner) },
                        {},
                        {
                            children: serviceProvider.employment.profession
                                ? translateKey(`professionEnum.${serviceProvider.employment.profession}`)
                                : '',
                        },
                        {
                            children:
                                serviceProvider.specializationDescriptions &&
                                serviceProvider.specializationDescriptions.join(', ').length > 30 ? (
                                    <Popover content={serviceProvider.specializationDescriptions?.join(', ')}>
                                        {`${serviceProvider.specializationDescriptions
                                            ?.join(', ')
                                            .substring(0, 30)}...`}
                                    </Popover>
                                ) : (
                                    serviceProvider.specializationDescriptions?.join(', ')
                                ),
                        },
                    ],
                };
            }
        );

        if (expandedServiceProvidersToShow.length < preferredProvidingOrganization.serviceProviders.length) {
            expandedTableRows.push({
                rowKey: `expandedTableRowShowMore_${preferredProvidingOrganization.organization.id}`,
                cells: [
                    {
                        children: (
                            <Link
                                onClick={() =>
                                    setAllServiceProvidersExpandedProviderIds([
                                        ...allServiceProvidersExpandedProviderIds,
                                        preferredProvidingOrganization.organization.id,
                                    ])
                                }
                                variant="button"
                            >
                                {translateKey('common.showMore')}
                            </Link>
                        ),
                    },
                    {},
                    {},
                    {},
                    {},
                ],
            });
        }

        return expandedTableRows;
    }
}
